/**
 * Onboarding Page
 * 
 * Shown to users who are authenticated but not yet part of any organization.
 * Allows them to:
 * - View and edit their profile
 * - Change their password
 * - Understand they need to be added to an organization
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  useTheme
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  User,
  Mail,
  Phone,
  Building2,
  Clock,
  LogOut,
  Save,
  CheckCircle,
  AlertCircle,
  Lock,
  KeyRound
} from 'lucide-react';
import { getAuth, signOut } from 'firebase/auth';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../config';
import { useAuthData } from '../contexts/AuthDataContext';

const Onboarding = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { userData, loading: authLoading, refreshUserData } = useAuthData();
  const auth = getAuth();

  // Profile state
  const [profile, setProfile] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phonenumber: ''
  });
  const [profileSaving, setProfileSaving] = useState(false);

  // Password state
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Load profile data
  useEffect(() => {
    if (userData) {
      setProfile({
        firstname: userData.firstname || '',
        lastname: userData.lastname || '',
        email: userData.email || '',
        phonenumber: userData.phonenumber || ''
      });
    }
  }, [userData]);

  // Check if user now has an organization (e.g., after being added by admin)
  useEffect(() => {
    if (userData?.organisation_id) {
      navigate('/dashboard', { replace: true });
    }
  }, [userData, navigate]);

  const handleProfileChange = (field) => (e) => {
    setProfile(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    try {
      setProfileSaving(true);
      const token = await auth.currentUser.getIdToken();

      await axios.put(
        `${BASE_URL}/api/users/me`,
        {
          firstname: profile.firstname,
          lastname: profile.lastname,
          phonenumber: profile.phonenumber
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Profile updated successfully');
      refreshUserData();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = (field) => (e) => {
    setPasswords(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleChangePassword = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwords.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setPasswordSaving(true);
      const token = await auth.currentUser.getIdToken();

      await axios.put(
        `${BASE_URL}/api/users/me/password`,
        {
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword,
          confirmPassword: passwords.confirmPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Password changed successfully. Please log in again.');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      await signOut(auth);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
        px: 2
      }}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img
            src="/assets/images/EN_Logo.png"
            alt="Logo"
            style={{ width: 140, height: 'auto', marginBottom: 16 }}
          />
          <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 700, mb: 1 }}>
            Welcome to Encode Nature
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Complete your profile setup
          </Typography>
        </Box>

        {/* Status Card */}
        <Card 
          elevation={0}
          sx={{ 
            mb: 3, 
            borderLeft: `4px solid ${theme.palette.warning.main}`,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderLeftColor: 'warning.main'
          }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AlertCircle size={24} color={theme.palette.warning.main} />
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                Pending Organization Assignment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You're not part of any organization yet. Please contact your administrator 
                to be added to an organization, or wait for an invitation.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Profile Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar
              sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.5rem' }}
            >
              {profile.firstname?.charAt(0)}{profile.lastname?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6" color="text.primary">
                {profile.firstname} {profile.lastname}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {profile.email}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <User size={18} />
            Profile Information
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="First Name"
              value={profile.firstname}
              onChange={handleProfileChange('firstname')}
              fullWidth
              size="small"
            />
            <TextField
              label="Last Name"
              value={profile.lastname}
              onChange={handleProfileChange('lastname')}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              value={profile.email}
              fullWidth
              size="small"
              disabled
              helperText="Email cannot be changed"
            />
            <TextField
              label="Phone Number"
              value={profile.phonenumber}
              onChange={handleProfileChange('phonenumber')}
              fullWidth
              size="small"
            />
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={profileSaving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
              onClick={handleSaveProfile}
              disabled={profileSaving}
            >
              Save Profile
            </Button>
          </Box>
        </Paper>

        {/* Password Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyRound size={18} />
            Change Password
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Current Password"
              type={showPasswords.current ? 'text' : 'password'}
              value={passwords.currentPassword}
              onChange={handlePasswordChange('currentPassword')}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}
                      size="small"
                    >
                      {showPasswords.current ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="New Password"
              type={showPasswords.new ? 'text' : 'password'}
              value={passwords.newPassword}
              onChange={handlePasswordChange('newPassword')}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                      size="small"
                    >
                      {showPasswords.new ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="Confirm New Password"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwords.confirmPassword}
              onChange={handlePasswordChange('confirmPassword')}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                      size="small"
                    >
                      {showPasswords.confirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleChangePassword}
              disabled={passwordSaving || !passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword}
              startIcon={passwordSaving ? <CircularProgress size={16} /> : null}
            >
              Change Password
            </Button>
          </Box>
        </Paper>

        {/* What's Next Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Clock size={18} />
            What Happens Next?
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CheckCircle size={20} color={theme.palette.success.main} style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                <strong style={{ color: theme.palette.text.primary }}>Your account is ready.</strong> You've successfully signed up and can update your profile anytime.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Building2 size={20} color={theme.palette.primary.main} style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                <strong style={{ color: theme.palette.text.primary }}>Wait for organization assignment.</strong> An administrator will add you to an organization. 
                You'll automatically get access once assigned.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Mail size={20} color={theme.palette.secondary.main} style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                <strong style={{ color: theme.palette.text.primary }}>Need help?</strong> Contact your organization administrator or email support.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Logout Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<LogOut size={18} />}
            onClick={handleLogout}
            sx={{ 
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': { 
                borderColor: 'text.secondary', 
                bgcolor: 'action.hover' 
              }
            }}
          >
            Sign Out
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Onboarding;
